import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import {
  InGameCurrency,
  Rarity,
  RewardConfigRarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import {
  ANY_SPORT_ESSENCE_TYPE,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_WHEEL,
  ANY_SPORT_SERIES_MARKET,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useGetMyCardsBoostLink } from '@sorare/core/src/hooks/my-cards/useGetMyCardsBoostLink';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';
import { fantasy, glossary, wheel } from '@sorare/core/src/lib/glossary';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import { getMonetaryAmountDisplayCurrency } from '@sorare/core/src/lib/monetaryAmount';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';
import { useIsSpecialCardSet } from '@sorare/core/src/set';

import {
  isCraftingClue,
  mergeRewardsConfig,
} from '@sorare/core/src/lib/rewards';
import { isGoldenWheelTicketCurrency } from '@sorare/core/src/lib/wheelCurrencies.ts';
import {
  DualRewardUnlockedStory,
  type RewardGroupDisplayProps,
} from './DualRewardUnlockedStory/index.tsx';
import { DumbRewardUnlockedStory } from './DumbRewardUnlockedStory.tsx';
import { MonetaryRewardConversion } from './MonetaryRewardConversion/index.tsx';
import type {
  RewardUnlockedStory_rewardConfig,
  RewardUnlockedStory_taskInterface,
} from './__generated__/index.graphql.ts';
import { useOnCommonEssenceClick } from './useOnCommonEssenceClick.ts';

const messages = defineMessages({
  clueTitle: {
    defaultMessage: "You've gained crafting clue!",
  },
  defaultTitle: {
    defaultMessage: "You've won a reward!",
  },
  xpTitle: {
    defaultMessage: "You've gained {rarity} XP!",
  },
  mixedXpTitle: {
    defaultMessage: "You've gained XP!",
  },
  xpSubtitle: {
    defaultMessage: 'Level up your players with XP.',
  },
  essenceTitle: {
    defaultMessage: "You've won {rarity} Essence!",
  },
  essenceSubtitle: {
    defaultMessage: 'Use Essence to craft new players.',
  },
  essenceSubtitleCommon: {
    defaultMessage: 'Use Essence to buy new packs.',
  },
  monetaryTitle: {
    defaultMessage: "You've won cash!",
  },
  marketCreditTitle: {
    defaultMessage: "You've won market credits!",
  },
  marketCreditSubTitle: {
    defaultMessage: 'Use them in Auctions or Instant Buy',
  },
  wheelTicketTitle: {
    defaultMessage:
      "You've won {rarity} {count, plural, one {wheel ticket} other {wheel tickets}}!",
  },
  goldenWheelTicketTitle: {
    defaultMessage:
      "You've won {rarity} {count, plural, one {golden wheel ticket} other {golden wheel tickets}}!",
  },
  wheelTicketSubtitle: {
    defaultMessage: 'Use them to spin the wheel.',
  },
  gemTitle: {
    defaultMessage: "You've won Gems!",
  },
});

type Props = {
  onClose: () => void;
  task: RewardUnlockedStory_taskInterface;
  alternativeRewardConfigs?: RewardUnlockedStory_rewardConfig[];
  showContinueButton?: boolean;
  action?: ReactNode;
  header?: ReactNode;
  rewardPictureUrl?: string | null;
  useQuaternaryColorForAction?: boolean;
};

export const RewardUnlockedStory = ({
  onClose,
  task,
  alternativeRewardConfigs,
  showContinueButton = true,
  action: actionProp,
  header,
  rewardPictureUrl,
  useQuaternaryColorForAction = false,
}: Props) => {
  const { formatNumber } = useIntlContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();
  const { sport, generateSportContextPath } = useSportContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  const { formatMessage } = useIntl();
  const { fiatCurrency } = useCurrentUserContext();
  const isSpecialCardSet = useIsSpecialCardSet();
  const onCommonEssenceClick = useOnCommonEssenceClick(!showContinueButton);
  const rewardConfigs = alternativeRewardConfigs || task.rewardConfigs;

  const mergedRewardConfigs = mergeRewardsConfig(rewardConfigs);
  if (mergedRewardConfigs.length === 0) {
    return null;
  }

  const rewardsGroupedByType = groupBy(rewardConfigs, r => r.__typename);
  const typeGroups = Object.values(rewardsGroupedByType).filter(
    (g): g is NonNullable<typeof g> => !!g
  );

  const buttonColor = useQuaternaryColorForAction ? 'quaternary' : 'primary';

  const getGroupDisplayProps = (
    groupConfigs: RewardUnlockedStory_rewardConfig[]
  ): RewardGroupDisplayProps<RewardUnlockedStory_rewardConfig> | null => {
    const merged = mergeRewardsConfig(groupConfigs);
    const first = merged.at(0);
    if (!first) return null;

    if (isType(first, 'InGameCurrencyRewardConfig')) {
      const inGameCurrencyConfigs = groupConfigs.filter(
        isTypeFn('InGameCurrencyRewardConfig')
      );
      const inGameCurrenciesGroupedByCurrency = groupBy(
        inGameCurrencyConfigs,
        r => r.currency
      );
      const rewardCountsByCurrency = Object.entries(
        inGameCurrenciesGroupedByCurrency
      ).reduce<Record<InGameCurrency, number>>(
        (acc, [currency, rewards]) => {
          acc[currency as InGameCurrency] = rewards.reduce<number>(
            (sum, curr) => sum + curr.amount,
            0
          );
          return acc;
        },
        {} as Record<InGameCurrency, number>
      );
      const rarityTyped =
        inGameCurrencyToRarity[
          Object.keys(rewardCountsByCurrency)[0] as InGameCurrency
        ];
      let amount: string;
      let title: ReactNode;
      let subtitle: string | undefined;
      let action: ReactNode;
      if (Object.keys(rewardCountsByCurrency).length === 1) {
        const currency = Object.keys(rewardCountsByCurrency).at(
          0
        ) as InGameCurrency;
        if (
          currency === InGameCurrency.COMMON_WHEEL_TICKET ||
          currency === InGameCurrency.WHEEL_TICKET
        ) {
          const rarity =
            currency === InGameCurrency.COMMON_WHEEL_TICKET
              ? Rarity.common
              : Rarity.limited;
          title = formatMessage(messages.wheelTicketTitle, {
            rarity: scarcityNames[rarity],
            count: Object.values(rewardCountsByCurrency).at(0),
          });
          subtitle = formatMessage(messages.wheelTicketSubtitle);
          action = (
            <Button
              size="large"
              to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL)}
              color={buttonColor}
            >
              <FormattedMessage {...wheel.spin} />
            </Button>
          );
        } else if (isGoldenWheelTicketCurrency(currency)) {
          title = formatMessage(messages.goldenWheelTicketTitle, {
            rarity:
              rarityTyped === Rarity.common
                ? // display nothing for common golden wheel ticket as common is not the right word in Sets
                  ''
                : scarcityNames[rarityTyped || Rarity.limited],
            count: Object.values(rewardCountsByCurrency).at(0),
          });
          subtitle = '';
          action = (
            <Button
              size="large"
              to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL)}
              color={buttonColor}
            >
              <FormattedMessage {...wheel.spin} />
            </Button>
          );
        } else if (isCraftingClue(currency)) {
          title = formatMessage(messages.clueTitle);
          subtitle = '';
          // No action button - show default "Continue" button
        } else if (
          InGameCurrency.COMMON_XP === currency ||
          InGameCurrency.LIMITED_XP === currency ||
          InGameCurrency.RARE_XP === currency ||
          InGameCurrency.SUPER_RARE_XP === currency ||
          InGameCurrency.UNIQUE_XP === currency
        ) {
          title = formatMessage(messages.xpTitle, {
            rarity: scarcityNames[rarityTyped || Rarity.common],
          });
          subtitle = formatMessage(messages.xpSubtitle);
          action = (
            <Button
              size="large"
              to={getMyCardsBoostLink({
                rarity: rarityTyped || Rarity.common,
                sport,
              })}
              color={buttonColor}
            >
              <FormattedMessage {...fantasy.levelUp} />
            </Button>
          );
        } else if (InGameCurrency.COMMON_GEM === currency) {
          title = formatMessage(messages.gemTitle);
          action = (
            <Button
              size="large"
              to={generateSportContextPath(ANY_SPORT_SERIES_MARKET)}
              color="primary"
            >
              <FormattedMessage {...glossary.goToMarket} />
            </Button>
          );
        } else {
          title = formatMessage(messages.defaultTitle);
        }
        amount = formatNumber(Object.values(rewardCountsByCurrency)[0]);
      } else {
        title = formatMessage(messages.mixedXpTitle);
        amount = formatNumber(
          Object.values(rewardCountsByCurrency).reduce(
            (acc, curr) => acc + curr,
            0
          )
        );
      }
      return {
        title: title || <FormattedMessage {...messages.defaultTitle} />,
        subtitle,
        amount,
        action,
        rgbColor:
          Object.keys(rewardCountsByCurrency).length === 1 && rarityTyped
            ? getRarityRGBColor(rarityTyped)
            : undefined,
        rewardConfigs: groupConfigs,
      };
    }

    if (isType(first, 'CardShardRewardConfig')) {
      const rarity = (
        first.configRarity !== RewardConfigRarity.unknown_rarity
          ? first.configRarity
          : Rarity.limited
      ) as Rarity;
      const isCommonPackEssence =
        first.configRarity === Rarity.common && sport !== Sport.BASEBALL;

      let action: ReactNode = (
        <Button
          to={generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
            params: { type: rarity.toLowerCase() },
          })}
          size="large"
          color={buttonColor}
        >
          <FormattedMessage defaultMessage="Craft players" />
        </Button>
      );

      if (isCommonPackEssence) {
        action = (
          <LoadingButton
            size="large"
            color={buttonColor}
            onClick={onCommonEssenceClick}
          >
            <FormattedMessage defaultMessage="Buy packs" />
          </LoadingButton>
        );
      }

      return {
        title: formatMessage(messages.essenceTitle, {
          rarity: isSpecialCardSet(first.cardSet?.slug)
            ? first.cardSet?.name
            : scarcityNames[rarity],
        }),
        subtitle: formatMessage(
          isCommonPackEssence
            ? messages.essenceSubtitleCommon
            : messages.essenceSubtitle
        ),
        amount: formatNumber(first.quantity),
        rgbColor: getRarityRGBColor(rarity),
        action,
        rewardConfigs: groupConfigs,
      };
    }

    if (isType(first, 'ConversionCreditRewardConfig')) {
      const { maxDiscount } = first;
      const usdAmount = formatNumber((maxDiscount.usdCents || 0) / 100, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });
      return {
        title: formatMessage(messages.marketCreditTitle),
        subtitle: formatMessage(messages.marketCreditSubTitle),
        amount: usdAmount,
        action: (
          <Button
            size="large"
            to={generateSportContextPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY)}
            color={buttonColor}
          >
            <FormattedMessage defaultMessage="Scout players" />
          </Button>
        ),
        rgbColor: '29, 60, 55',
        rewardConfigs: groupConfigs,
      };
    }

    if (isType(first, 'MonetaryRewardConfig')) {
      const monetaryAmount = toMonetaryAmount(first.monetaryAmount);
      const displayCurrency = getMonetaryAmountDisplayCurrency(
        first.monetaryAmount,
        fiatCurrency
      );
      const totalCash = monetaryAmount.toMajorUnit(displayCurrency);
      const displayAmount =
        first.sharedPool && first.poolSize > 0
          ? totalCash / first.poolSize
          : totalCash;
      const formattedTotalCash = formatNumber(displayAmount, {
        style: 'currency',
        currency: displayCurrency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0,
      });
      return {
        title: formatMessage(messages.monetaryTitle),
        amount: formattedTotalCash,
        rgbColor: '49, 42, 99',
        children: (
          <MonetaryRewardConversion
            displayAmount={displayAmount}
            displayCurrency={displayCurrency}
          />
        ),
        rewardConfigs: groupConfigs,
      };
    }

    return {
      title: <FormattedMessage {...messages.defaultTitle} />,
      rewardConfigs: groupConfigs,
    };
  };

  if (typeGroups.length === 2) {
    const group1Props = getGroupDisplayProps(typeGroups[0]);
    const group2Props = getGroupDisplayProps(typeGroups[1]);
    if (group1Props && group2Props) {
      return (
        <DualRewardUnlockedStory
          header={header}
          onClose={onClose}
          group1Props={group1Props}
          group2Props={group2Props}
          actionProp={actionProp}
          showContinueButton={showContinueButton}
          useQuaternaryColorForAction={useQuaternaryColorForAction}
        />
      );
    }
  }

  const props = getGroupDisplayProps(rewardConfigs);
  if (props) {
    return (
      <DumbRewardUnlockedStory
        onClose={onClose}
        header={header}
        rewardConfigs={props.rewardConfigs}
        title={props.title}
        subtitle={props.subtitle}
        amount={props.amount}
        rgbColor={props.rgbColor}
        showContinueButton={showContinueButton}
        action={actionProp || props.action}
        rewardPictureUrl={rewardPictureUrl}
        useQuaternaryColorForAction={useQuaternaryColorForAction}
      >
        {props.children}
      </DumbRewardUnlockedStory>
    );
  }

  return (
    <DumbRewardUnlockedStory
      onClose={onClose}
      header={header}
      rewardConfigs={rewardConfigs}
      showContinueButton={showContinueButton}
      action={actionProp}
      rewardPictureUrl={rewardPictureUrl}
      useQuaternaryColorForAction={useQuaternaryColorForAction}
    />
  );
};

const fragments = {
  rewardConfigs: gql`
    fragment RewardUnlockedStory_rewardConfig on AnyRewardConfigInterface {
      id
      ... on InGameCurrencyRewardConfig {
        id
        currency
        amount
      }
      ... on CardShardRewardConfig {
        id
        quantity
        configRarity
        cardSet {
          slug
          name
        }
      }
      ... on MonetaryRewardConfig {
        id
        poolSize
        sharedPool
        monetaryAmount: amount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on ConversionCreditRewardConfig {
        id
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ...DumbRewardUnlockedStory_rewardConfig
      ...mergeRewardsConfig_anyRewardConfigInterface
      ...DualRewardUnlockedStory_rewardConfig
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${DumbRewardUnlockedStory.fragments.rewardConfig}
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
    ${DualRewardUnlockedStory.fragments.rewardConfig}
  ` as TypedDocumentNode<RewardUnlockedStory_rewardConfig>,
};

RewardUnlockedStory.fragments = {
  taskInterface: gql`
    fragment RewardUnlockedStory_taskInterface on TaskInterface {
      id

      rewardConfigs {
        id
        ...RewardUnlockedStory_rewardConfig
      }
      alternativeRewardConfigs {
        rewardConfigs {
          id
          ...RewardUnlockedStory_rewardConfig
        }
      }
    }
    ${fragments.rewardConfigs}
  ` as TypedDocumentNode<RewardUnlockedStory_taskInterface>,
};
